import { Fragment, useEffect, useRef, MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import { eClientFeaturePermissions } from '../common/Components/FeaturesRestriction/Enums/eClientFeaturePermissions';
import { isTesting, isRenderedAsIframeInNewUI } from '../config';

type LoadedSnippets = Record<number, boolean>;
type LoadedSnippetsRef = MutableRefObject<LoadedSnippets>;
type EnabledFeatures = number[];

const isCynetEmployee = (email: string) => email?.endsWith('@cynet.com') ?? false;

const featuresSettings = {
  [eClientFeaturePermissions.LoadGoogleTagManager.value]: {
    import: async () => await import('./external/GTM'),
    isValid: () => true
  },
  [eClientFeaturePermissions.ShowDelightedPanel.value]: {
    import: async () => await import('./external/Delighted'),
    isValid: (userName: string) => !isCynetEmployee(userName)
  },
  [eClientFeaturePermissions.ShowElevioSurvey.value]: {
    import: async () => await import('./external/Elevio'),
    isValid: () => !isTesting && !isRenderedAsIframeInNewUI
  },
  [eClientFeaturePermissions.LoadGATag.value]: {
    import: async () => await import('./external/GA'),
    isValid: (userName: string) => !isCynetEmployee(userName)
  },
  [eClientFeaturePermissions.LoadPendoTag.value]: {
    import: async () => await import('./external/Pendo'),
    isValid: (userName: string) => !isCynetEmployee(userName)
  }
};

const executeSnippets = (
  enabledFeatures: EnabledFeatures,
  loadedSnippetsRef: LoadedSnippetsRef,
  loggedUser: Record<string, any>
) => {
  enabledFeatures
    ?.filter((featureKey: number) => featuresSettings[featureKey]?.isValid(loggedUser?.userName))
    ?.forEach(async (featureKey: number) => {
      const featureSettings = featuresSettings[featureKey];

      if (featureSettings.import && !loadedSnippetsRef.current[featureKey]) {
        const app = await featureSettings.import();
        app.default(loggedUser);
        loadedSnippetsRef.current[featureKey] = true;
      }
    });
};

function PluginsLoader() {
  const loggedUser = useSelector((state: any) => state.user.loggedUser);
  const enabledFeatures = useSelector((state: any) => state.user.loggedUser.enabledFeatures);

  const loadedSnippetsRef = useRef<LoadedSnippets>({
    [eClientFeaturePermissions.LoadGoogleTagManager.value]: false,
    [eClientFeaturePermissions.ShowDelightedPanel.value]: false,
    [eClientFeaturePermissions.ShowElevioSurvey.value]: false,
    [eClientFeaturePermissions.LoadGATag.value]: false,
    [eClientFeaturePermissions.LoadPendoTag.value]: false
  });

  useEffect(() => {
    if (loggedUser?.userName) {
      executeSnippets(enabledFeatures, loadedSnippetsRef, loggedUser);
    }
  }, [enabledFeatures, loggedUser]);

  return <Fragment />;
}

export default PluginsLoader;
